'use client'

import { ParsedTebutoRuleEvent, ParsedTebutoStandaloneEvent } from '@tebuto/functions/calendar'
import { DateTime } from 'luxon'
import { createContext, Dispatch, SetStateAction, useState } from 'react'
import { type EventRuleCreationMode, EventRuleForm } from './event-rule-form'
import { CalendarConfiguration } from './hooks'

export const CALENDAR_SETTINGS_LOCAL_STORAGE_KEY = 'calendar-settings'

export type DragEventInfo = {
    event: ParsedTebutoRuleEvent | ParsedTebutoStandaloneEvent
    targetDateTime: DateTime<true>
    update?: Partial<EventRuleForm>
    revert?: () => void
}
export type ClientListContextData = {
    calendarConfiguration: CalendarConfiguration
    setCalendarConfiguration: Dispatch<SetStateAction<CalendarConfiguration>>
    startDate: DateTime<true>
    setStartDate: (date: DateTime<true>) => void
    endDate: DateTime<true>
    setEndDate: (date: DateTime<true>) => void
    isAddEventRuleModalOpen: boolean
    setIsAddEventRuleModalOpen: Dispatch<SetStateAction<boolean>>
    isEditEventRuleModalOpen: boolean
    setIsEditEventRuleModalOpen: Dispatch<SetStateAction<boolean>>
    isEventDetailsModalOpen: boolean
    setIsEventDetailsModalOpen: Dispatch<SetStateAction<boolean>>
    isMoveEventDecisionModalOpen: boolean
    setIsMoveEventDecisionModalOpen: Dispatch<SetStateAction<boolean>>
    selectedEvent: ParsedTebutoRuleEvent | ParsedTebutoStandaloneEvent | undefined
    setSelectedEvent: Dispatch<SetStateAction<ParsedTebutoRuleEvent | ParsedTebutoStandaloneEvent | undefined>>
    dragEventStart: DateTime<true> | undefined
    setDragEventStart: Dispatch<SetStateAction<DateTime<true> | undefined>>
    dragEventInfo: DragEventInfo | undefined
    setDragEventInfo: Dispatch<SetStateAction<DragEventInfo | undefined>>
    highlightedEventStart: DateTime<true> | undefined
    setHighlightedEventStart: Dispatch<SetStateAction<DateTime<true> | undefined>>
    lockedEventRuleCreationMode: EventRuleCreationMode | null
}

export const CalendarContext = createContext<ClientListContextData>({} as ClientListContextData)

export function CalendarContextProvider({
    children,
    lockedEventRuleCreationMode = null
}: {
    children: React.ReactNode
    lockedEventRuleCreationMode?: EventRuleCreationMode | null
}) {
    const [calendarConfiguration, setCalendarConfiguration] = useState<CalendarConfiguration>(
        getInitialCalendarFormValues()
    )

    const { initialStartDate, initialEndDate } = getInitialDates(calendarConfiguration)
    const [startDate, setStartDate] = useState<DateTime<true>>(initialStartDate)
    const [endDate, setEndDate] = useState<DateTime<true>>(initialEndDate)

    const [isAddEventRuleModalOpen, setIsAddEventRuleModalOpen] = useState<boolean>(false)
    const [isEditEventRuleModalOpen, setIsEditEventRuleModalOpen] = useState<boolean>(false)
    const [isEventDetailsModalOpen, setIsEventDetailsModalOpen] = useState<boolean>(false)
    const [isMoveEventDecisionModalOpen, setIsMoveEventDecisionModalOpen] = useState<boolean>(false)
    const [selectedEvent, setSelectedEvent] = useState<ParsedTebutoRuleEvent | ParsedTebutoStandaloneEvent>()
    const [dragEventStart, setDragEventStart] = useState<DateTime<true>>()
    const [dragEventInfo, setDragEventInfo] = useState<DragEventInfo>()
    const [highlightedEventStart, setHighlightedEventStart] = useState<DateTime<true>>()

    function customSetStartDate(date: DateTime<true>) {
        setStartDate(date)
        localStorage.setItem('calendar-start-date', date.toISODate())
    }

    function customSetEndDate(date: DateTime<true>) {
        setEndDate(date)
        localStorage.setItem('calendar-end-date', date.toISODate())
    }

    return (
        <CalendarContext.Provider
            value={{
                calendarConfiguration,
                setCalendarConfiguration,
                startDate,
                setStartDate: customSetStartDate,
                endDate,
                setEndDate: customSetEndDate,
                isAddEventRuleModalOpen,
                setIsAddEventRuleModalOpen,
                isEditEventRuleModalOpen,
                setIsEditEventRuleModalOpen,
                isEventDetailsModalOpen,
                setIsEventDetailsModalOpen,
                isMoveEventDecisionModalOpen,
                setIsMoveEventDecisionModalOpen,
                selectedEvent,
                setSelectedEvent,
                dragEventStart,
                setDragEventStart,
                dragEventInfo,
                setDragEventInfo,
                highlightedEventStart,
                setHighlightedEventStart,
                lockedEventRuleCreationMode
            }}
        >
            {children}
        </CalendarContext.Provider>
    )
}

function getInitialCalendarFormValues(): CalendarConfiguration {
    const storedValues = localStorage.getItem(CALENDAR_SETTINGS_LOCAL_STORAGE_KEY)
    if (storedValues) {
        return JSON.parse(storedValues)
    }
    return {
        showWeekends: false,
        showBookedAppointments: true,
        showFreeAppointments: true,
        showBookableInformation: true,
        currentView: 'timeGridWeek',
        startTime: '06:00',
        endTime: '22:00'
    }
}

function getInitialDates(calendarConfiguration: CalendarConfiguration): {
    initialStartDate: DateTime<true>
    initialEndDate: DateTime<true>
} {
    const localStoragecalendarStartDate = DateTime.fromISO(localStorage.getItem('calendar-start-date') || '')
    const localStoragecalendarEndDate = DateTime.fromISO(localStorage.getItem('calendar-end-date') || '')
    const initialStartDate: DateTime<true> =
        localStoragecalendarStartDate.isValid &&
        localStoragecalendarStartDate.startOf('day') >= DateTime.now().startOf('day')
            ? (localStoragecalendarStartDate as DateTime<true>)
            : DateTime.now().startOf('week')
    const initialEndDate: DateTime<true> =
        localStoragecalendarEndDate.isValid &&
        localStoragecalendarStartDate.startOf('day') >= DateTime.now().startOf('day')
            ? (localStoragecalendarEndDate as DateTime<true>)
            : initialStartDate.plus({ days: calendarConfiguration.showWeekends ? 7 : 5 })
    return { initialStartDate, initialEndDate }
}
